import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import MaterialTable from 'material-table';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
    Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    MenuItem, Select, TextField, Typography,
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import moment from 'moment';

import {
    CatalogOrderTempProduct,
    CatalogOrderTempProductMinProduct,
    CatalogOrderTempProductStatus,
} from '../../models';
import { PaginatedResponse } from '../../../shared/models';
import {
    getCatalogOrderTempProducts,
    decideCatalogOrderTempProduct,
    undoCatalogOrderTempProductDecision,
} from '../../api';
import { getProductsByPage } from '../../../products/api/Api';
import { checkStatus, parseJSON, handleError } from '../../../shared/api/core/common';
import { SVG_ICONS } from '../../../shared/icons/SvgIcons';
import { ErrorStatusBar, CustomTableTitle } from '../../../shared/components';
import { Toast, enqueueSnackbar, ToastSuccess, ToastError } from '../../../shared/components/Notifier';
import { EMPTY_ROW_MSG, ERROR_500, ERROR_MSG, NO_PERMISSIONS_ERROR_MSG } from '../../../shared/lib/Localization';
import { replaceHistory } from '../../../shared/lib/TableQueryParams';
import { BusinessRouterPaths } from '../../../page/Routes/RouterPaths';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        statusSelect: {
            minWidth: '10rem',
        },
        duplicateProduct: {
            marginTop: 12,
        },
        decisionControls: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
        },
    }),
);

const STATUS_FILTER_OPTIONS = [
    { value: CatalogOrderTempProductStatus.PENDING, label: 'Pending' },
    { value: CatalogOrderTempProductStatus.APPROVED, label: 'Approved' },
    { value: CatalogOrderTempProductStatus.DUPLICATE, label: 'Duplicate' },
    { value: 'ALL', label: 'All' },
];

// Data Team review queue for Products auto-created by catalog orders. A row is
// either approved in place or marked duplicate with an existing replacement.
// Duplicate decisions create tracking tasks; users make the actual changes in
// Korona from the separate stock-order task view.
const CatalogOrderReviewScreen = (props: any) => {
    const classes = useStyles({});
    const [emptyDataSourceMessage, setEmptyDataSourceMessage] = useState(EMPTY_ROW_MSG);
    const [isErrorStatusVisible, setErrorStatusVisible] = useState(false);
    const tableRef = useRef<{ onQueryChange: any; }>();
    const [isLoading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState(CatalogOrderTempProductStatus.PENDING);
    const [duplicateRow, setDuplicateRow] = useState<CatalogOrderTempProduct | null>(null);
    const [approveRow, setApproveRow] = useState<CatalogOrderTempProduct | null>(null);
    const [undoRow, setUndoRow] = useState<CatalogOrderTempProduct | null>(null);
    const [replacementProduct, setReplacementProduct] =
        useState<CatalogOrderTempProductMinProduct | null>(null);
    const [isSubmittingDecision, setSubmittingDecision] = useState(false);

    useEffect(() => {
        if (!isLoading) {
            tableRef.current && tableRef.current.onQueryChange();
        }
    }, [props.userData.selectedBusiness, statusFilter]);

    const showToast = (message: string, options = ToastError) => {
        props.showToast({ message, options });
    };

    const refreshTable = () => {
        tableRef.current && tableRef.current.onQueryChange();
    };

    const handleDecisionError = (error: any, fallback: string) => {
        let msg = error && error.body && error.body.error ? error.body.error : fallback;
        if (error && (error.status === 403 || error.status === 401)) {
            msg = NO_PERMISSIONS_ERROR_MSG;
        }
        showToast(msg);
    };

    const handleApprove = () => {
        if (!approveRow) {
            return;
        }
        setLoading(true);
        decideCatalogOrderTempProduct(approveRow.id, CatalogOrderTempProductStatus.APPROVED)
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then(() => {
                setLoading(false);
                setApproveRow(null);
                showToast('Temp product approved.', ToastSuccess);
                refreshTable();
            })
            .catch((error: any) => {
                setLoading(false);
                handleDecisionError(error, 'Failed to approve temp product.');
            });
    };

    const handleUndo = () => {
        if (!undoRow) {
            return;
        }
        setSubmittingDecision(true);
        undoCatalogOrderTempProductDecision(undoRow.id)
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then(() => {
                setSubmittingDecision(false);
                setUndoRow(null);
                showToast('Temp product decision undone.', ToastSuccess);
                refreshTable();
            })
            .catch((error: any) => {
                setSubmittingDecision(false);
                handleDecisionError(error, 'Failed to undo the temp product decision.');
            });
    };

    const openDuplicateDialog = (row: CatalogOrderTempProduct) => {
        setDuplicateRow(row);
        setReplacementProduct(null);
    };

    const closeDuplicateDialog = () => {
        setDuplicateRow(null);
        setReplacementProduct(null);
    };

    const handleSubmitDuplicate = () => {
        if (!duplicateRow || !replacementProduct || !replacementProduct.uuid) {
            showToast('Please choose a replacement product.');
            return;
        }
        setSubmittingDecision(true);
        decideCatalogOrderTempProduct(
            duplicateRow.id,
            CatalogOrderTempProductStatus.DUPLICATE,
            replacementProduct.uuid,
        )
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then((data: any) => {
                setSubmittingDecision(false);
                closeDuplicateDialog();
                const correctionCount = data && data.correction_count != null ? data.correction_count : 0;
                showToast(
                    correctionCount > 0
                        ? `Marked as duplicate. ${correctionCount} correction task(s) created.`
                        : 'Marked as duplicate.',
                    ToastSuccess,
                );
                refreshTable();
            })
            .catch((error: any) => {
                setSubmittingDecision(false);
                handleDecisionError(error, 'Failed to mark temp product as duplicate.');
            });
    };

    const renderDuplicateDialog = () => {
        return (
            <Dialog open={!!duplicateRow} onClose={closeDuplicateDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Mark as Duplicate</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Choose the existing product that should replace{' '}
                        {duplicateRow && duplicateRow.product
                            ? `${duplicateRow.product.number} - ${duplicateRow.product.name}`
                            : 'this temp product'}
                        . Correction tasks will be created for any affected Korona stock orders.
                    </DialogContentText>
                    {duplicateRow && (
                        <Typography variant="caption" color="textSecondary" display="block">
                            Reorder Code: {duplicateRow.reorder_code || '-'} | Case Qty: {duplicateRow.case_qty || '-'}
                        </Typography>
                    )}
                    <div className={classes.duplicateProduct}>
                        <ProductSearchAutocomplete
                            businessId={props.userData.selectedBusiness && props.userData.selectedBusiness.business_id}
                            value={replacementProduct}
                            onChange={setReplacementProduct}
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDuplicateDialog} disabled={isSubmittingDecision}>Cancel</Button>
                    <Button
                        onClick={handleSubmitDuplicate}
                        color="primary"
                        variant="contained"
                        disabled={isSubmittingDecision || !replacementProduct}
                    >
                        {isSubmittingDecision ? <CircularProgress size={20} /> : 'Confirm Duplicate'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const renderApproveDialog = () => (
        <Dialog open={!!approveRow} onClose={() => setApproveRow(null)} maxWidth="xs" fullWidth>
            <DialogTitle>Confirm Approval</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Approve{' '}
                    {approveRow && approveRow.product
                        ? `${approveRow.product.number} - ${approveRow.product.name}`
                        : 'this temp product'}
                    ?
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setApproveRow(null)} disabled={isLoading}>Cancel</Button>
                <Button
                    onClick={handleApprove}
                    color="primary"
                    variant="contained"
                    disabled={isLoading}
                >
                    {isLoading ? <CircularProgress size={20} /> : 'Confirm Approve'}
                </Button>
            </DialogActions>
        </Dialog>
    );

    const renderUndoDialog = () => (
        <Dialog open={!!undoRow} onClose={() => setUndoRow(null)} maxWidth="xs" fullWidth>
            <DialogTitle>Undo Decision?</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Return this product to Pending? Any pending correction tasks created for this
                    product will be removed.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setUndoRow(null)} disabled={isSubmittingDecision}>Cancel</Button>
                <Button
                    onClick={handleUndo}
                    color="secondary"
                    variant="contained"
                    disabled={isSubmittingDecision}
                >
                    {isSubmittingDecision ? <CircularProgress size={20} /> : 'Confirm Undo'}
                </Button>
            </DialogActions>
        </Dialog>
    );

    return (
        <div>
            <ErrorStatusBar isVisible={isErrorStatusVisible} />
            <div className={classes.decisionControls}>
                <Select
                    className={classes.statusSelect}
                    value={statusFilter}
                    onChange={(e: any) => setStatusFilter(e.target.value)}
                >
                    {STATUS_FILTER_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                </Select>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={() => props.history.push(BusinessRouterPaths.catalogOrderCorrectionTasks)}
                >
                    View Stock Order Tasks
                </Button>
            </div>
            <div style={{ maxWidth: '100%' }}>
                <MaterialTable
                    options={{
                        debounceInterval: 650,
                        pageSize: 10,
                        pageSizeOptions: [10, 50, 100, 500],
                        headerStyle: { backgroundColor: '#eee' },
                    }}
                    tableRef={tableRef}
                    localization={{ body: { emptyDataSourceMessage } }}
                    columns={[
                        {
                            title: 'Created',
                            field: 'created_at',
                            render: (rowData: CatalogOrderTempProduct) =>
                                rowData.created_at
                                    ? <span>{moment(rowData.created_at).format('YYYY-MM-DD HH:mm')}</span>
                                    : <span></span>,
                        },
                        {
                            title: 'Stock Order #',
                            field: 'stock_order_number',
                        },
                        {
                            title: 'Product',
                            field: 'product.name',
                            render: (rowData: CatalogOrderTempProduct) =>
                                <span>{rowData.product ? `${rowData.product.number} - ${rowData.product.name}` : ''}</span>,
                        },
                        {
                            title: 'Master Product',
                            field: 'master_product.name',
                            render: (rowData: CatalogOrderTempProduct) =>
                                <span>{rowData.master_product ? rowData.master_product.name : ''}</span>,
                        },
                        { title: 'Reorder Code', field: 'reorder_code' },
                        { title: 'Case Qty', field: 'case_qty' },
                        { title: 'Status', field: 'status' },
                        {
                            title: 'Resolved To',
                            field: 'resolved_to_product.name',
                            render: (rowData: CatalogOrderTempProduct) =>
                                rowData.resolved_to_product
                                    ? <span>{`${rowData.resolved_to_product.number} - ${rowData.resolved_to_product.name}`}</span>
                                    : <span></span>,
                        },
                    ]}
                    data={(query) =>
                        new Promise((resolve) => {
                            if (isLoading) {
                                return;
                            }
                            const sb = props.userData.selectedBusiness;
                            if (!sb || !sb.business_id) {
                                resolve({ data: [], totalCount: 0, page: query.page });
                                return;
                            }
                            replaceHistory(query, props.history);
                            setLoading(true);
                            getCatalogOrderTempProducts(sb.business_id, query.page + 1, query.pageSize, statusFilter)
                                .catch(handleError)
                                .then(checkStatus)
                                .then(parseJSON)
                                .then((data: PaginatedResponse<CatalogOrderTempProduct>) => {
                                    setErrorStatusVisible(false);
                                    setEmptyDataSourceMessage(EMPTY_ROW_MSG);
                                    setLoading(false);
                                    resolve({ data: data.results, totalCount: data.count, page: query.page });
                                })
                                .catch((error: any) => {
                                    if (error) {
                                        if (error.status === 500) {
                                            setEmptyDataSourceMessage(ERROR_500);
                                            setErrorStatusVisible(false);
                                        } else if (error.status === 403 || error.status === 401) {
                                            setEmptyDataSourceMessage(ERROR_MSG);
                                            setErrorStatusVisible(true);
                                        } else {
                                            setEmptyDataSourceMessage(ERROR_MSG);
                                            setErrorStatusVisible(false);
                                        }
                                    }
                                    setLoading(false);
                                    resolve({ data: [], totalCount: 0, page: query.page });
                                });
                        })
                    }
                    title={
                        <CustomTableTitle
                            history={props.history}
                            title={'Stock Order Temp Products'}
                            icon={SVG_ICONS.product} />
                    }
                    actions={[
                        {
                            icon: 'refresh',
                            tooltip: 'Refresh Data',
                            isFreeAction: true,
                            onClick: () => refreshTable(),
                        },
                        (rowData: CatalogOrderTempProduct) => ({
                            icon: 'thumb_up',
                            tooltip: 'Approve',
                            hidden: rowData.status !== CatalogOrderTempProductStatus.PENDING,
                            onClick: (_event: any, row: CatalogOrderTempProduct) => setApproveRow(row),
                        }),
                        (rowData: CatalogOrderTempProduct) => ({
                            icon: 'compare_arrows',
                            tooltip: 'Mark as Duplicate',
                            hidden: rowData.status !== CatalogOrderTempProductStatus.PENDING,
                            onClick: (_event: any, row: CatalogOrderTempProduct) => openDuplicateDialog(row),
                        }),
                        (rowData: CatalogOrderTempProduct) => ({
                            icon: 'undo',
                            tooltip: 'Undo Decision',
                            hidden:
                                rowData.status === CatalogOrderTempProductStatus.PENDING
                                || !rowData.can_undo,
                            onClick: (_event: any, row: CatalogOrderTempProduct) => setUndoRow(row),
                        }),
                    ]}
                />
            </div>
            {renderDuplicateDialog()}
            {renderApproveDialog()}
            {renderUndoDialog()}
        </div>
    );
};

interface ProductSearchAutocompleteProps {
    businessId: number | string;
    value: CatalogOrderTempProductMinProduct | null;
    onChange: (product: CatalogOrderTempProductMinProduct | null) => void;
}

// Single-select, search-as-you-type product picker used to choose the
// replacement product for a duplicate decision.
const ProductSearchAutocomplete = (props: ProductSearchAutocompleteProps) => {
    const { businessId, value, onChange } = props;
    const [options, setOptions] = useState<CatalogOrderTempProductMinProduct[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let active = true;
        if (!businessId || !inputValue || inputValue.trim().length < 2) {
            setOptions([]);
            return undefined;
        }
        setLoading(true);
        getProductsByPage(1, 20, inputValue.trim(), businessId)
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then((data: PaginatedResponse<CatalogOrderTempProductMinProduct>) => {
                if (active) {
                    setOptions(data && data.results ? data.results : []);
                    setLoading(false);
                }
            })
            .catch(() => {
                if (active) {
                    setOptions([]);
                    setLoading(false);
                }
            });
        return () => {
            active = false;
        };
    }, [inputValue, businessId]);

    return (
        <Autocomplete
            size="small"
            value={value}
            options={options}
            loading={loading}
            getOptionLabel={(option: CatalogOrderTempProductMinProduct) =>
                option ? `${option.number} - ${option.name}` : ''}
            getOptionSelected={(
                option: CatalogOrderTempProductMinProduct,
                selectedValue: CatalogOrderTempProductMinProduct,
            ) => option && selectedValue && option.uuid === selectedValue.uuid}
            onChange={(
                _event: React.ChangeEvent<{}>,
                newValue: CatalogOrderTempProductMinProduct | null,
            ) => onChange(newValue)}
            onInputChange={(_event: any, newInputValue: string) => setInputValue(newInputValue)}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Search replacement product"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="primary" size={16} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};

const mapStateToProps = (state: any) => {
    return {
        userData: state.userLocalData,
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return {
        showToast: (toast: Toast) => dispatch(enqueueSnackbar(toast)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(CatalogOrderReviewScreen);
